import { DvToolbarAction, DvToolbarActionEvent } from '@devinco/ui/grid';
import { Injectable, inject } from '@angular/core';
import { VehicleType } from '@shared/constants/vehicle-type';
import { TranslateService } from '@ngx-translate/core';
import { CellStyle, ColDef } from 'ag-grid-community';
import { VehicleDto } from './interfaces';
import { ColumnType } from '@features/vt-grid/grid-options/column-types';
import NextServiceUtils from '@shared/utils/next-service.utils';
import { DvSplitterEventsService } from '@devinco/ui/dds/splitter';
import { TextMatcherParams } from 'ag-grid-enterprise';
import { getStandardActionPreset } from '@features/vt-grid/presets/preset-actions';
import { UserMetadataService } from '@core/services/user-metadata.service';
import { isElectricFuelType } from '@shared/constants/fuel-types';

@Injectable({ providedIn: 'root' })
export class VehiclesGridService {
  private splitterEvents = inject(DvSplitterEventsService);
  private translate = inject(TranslateService);
  private metaDataService = inject(UserMetadataService);

  getToolbarActions(
    showOnMap: () => void,
    deleteSelectedRow: (event: DvToolbarActionEvent) => void
  ): DvToolbarAction[] {
    return [
      ...getStandardActionPreset(),
      {},
      {
        icon: 'map_marker_move',
        tooltipKey: 'VehiclesGrid.ShowVehicleOnMap',
        clickFn: showOnMap,
        disabled: true,
      },
      {
        icon: 'delete',
        clickFn: (event: DvToolbarActionEvent) => deleteSelectedRow(event),
        disabled: true,
        tooltipKey: 'Grid.Delete',
      },
      {
        icon: 'edit',
        clickFn: () => this.splitterEvents.isSplitterOpened$.next(true),
        disabled: true,
        tooltipKey: 'Grid.Edit',
      },
    ];
  }

  getColDefs = (): ColDef<VehicleDto>[] => [
    {
      field: 'VehicleID',
      headerName: 'Vehicle.VehicleID',
      hide: true,
      type: [ColumnType.Id],
    },
    {
      headerName: 'Vehicle.Picture',
      field: 'DocumentThumbnail',
      type: [ColumnType.Thumbnail],
      enableRowGroup: false,
      hide: true,
    },
    {
      field: 'RegistrationNumber',
      headerName: 'Vehicle.RegistrationNumber',
      type: [ColumnType.Text],
      filterParams: {
        defaultOption: 'contains',
        filterOptions: [
          'equals',
          'notEqual',
          'contains',
          'notContains',
          'startsWith',
          'endsWith',
        ],
        textMatcher: ({
          value,
          filterText,
          filterOption,
        }: TextMatcherParams) => {
          const normalizedFilterText = filterText
            ?.replace(/\s+/g, '')
            .toLowerCase();
          const normalizedValue = (value || '')
            .replace(/\s+/g, '')
            .toLowerCase();

          if (!value || !filterText) return false;

          switch (filterOption) {
            case 'contains':
              return (
                normalizedValue.includes(normalizedFilterText) ||
                value.includes(filterText)
              );
            case 'equals':
              return (
                normalizedValue === normalizedFilterText || value === filterText
              );
            case 'notEqual':
              return (
                normalizedValue !== normalizedFilterText || value !== filterText
              );
            case 'startsWith':
              return (
                normalizedValue.startsWith(normalizedFilterText) ||
                value.startsWith(filterText)
              );
            case 'endsWith':
              return (
                normalizedValue.endsWith(normalizedFilterText) ||
                value.endsWith(filterText)
              );
            case 'notContains':
              return (
                !normalizedValue.includes(normalizedFilterText!) ||
                !value.includes(filterText!)
              );
            default:
              return false;
          }
        },
      },
    },
    {
      field: 'UserDescription',
      headerName: 'Vehicle.UserDescription',
      type: [ColumnType.Text],
    },
    {
      field: 'VehicleDescription',
      headerName: 'Vehicle.VehicleDescription',
      type: [ColumnType.Text],
      enableRowGroup: false,
    },
    {
      field: 'TrackerSerial',
      headerName: 'Vehicle.TrackerSerial',
      type: [ColumnType.Text],
    },
    {
      field: 'VehicleTypeDescription',
      headerName: 'Vehicle.VehicleTypeID',
      type: [ColumnType.TranslatedText],
      filterParams: {
        maxNumConditions: 5,
        values: Object.values(VehicleType).map(value =>
          this.translate.instant(`VehicleType.${value}`)
        ),
      },
      filter: 'agSetColumnFilter',
      cellStyle: { 'text-align': 'left' },
    },
    {
      field: 'LastActivityTime',
      headerName: 'Vehicle.LastActivityTime',
      enableRowGroup: false,
      type: [ColumnType.DateTime],
    },
    {
      field: 'LastPositionTime',
      headerName: 'Vehicle.LastPositionTime',
      type: [ColumnType.DateTime],
    },
    { field: 'Mileage', headerName: 'Vehicle.Mileage', type: [ColumnType.Km] },
    {
      field: 'MileageLastAdjustment',
      headerName: 'Vehicle.MileageLastAdjustment',
      enableRowGroup: false,
      type: [ColumnType.DateTime],
    },
    {
      field: 'ServiceEUInspectionNextDate',
      headerName: 'VehicleServiceGrid.ServiceEUInspectionNextDate',
      type: [ColumnType.DateVT],
    },
    {
      field: 'Comment',
      headerName: 'Vehicle.Comment',
      type: [ColumnType.Text],
      enableRowGroup: false,
    },
    {
      field: 'RegistrationDate',
      headerName: 'Vehicle.RegistrationDate',
      type: [ColumnType.DateVT],
      enableRowGroup: false,
      hide: true,
    },
    {
      field: 'RegistrationYear',
      headerName: 'Vehicle.RegistrationYear',
      type: [ColumnType.NumberText],
      hide: true,
    },
    {
      field: 'DepartmentDescription',
      headerName: 'Vehicle.DepartmentDescription',
      type: [ColumnType.Text],
      cellClass: ({ data }) =>
        data?.DepartmentDeleted ? 'vt-deleted-row' : '',
      hide: true,
    },
    {
      field: 'DepartmentIDText',
      headerName: 'Vehicle.DepartmentIDText',
      type: [ColumnType.Text],
      hide: true,
    },
    {
      field: 'Username',
      headerName: 'Vehicle.Username',
      type: [ColumnType.Text],
      hide: true,
    },
    {
      field: 'UserStatusTranslationKey',
      headerName: 'Vehicle.UserStatus',
      type: [ColumnType.UserStatus],
      hide: true,
    },
    {
      field: 'UserMobile',
      headerName: 'Vehicle.UserMobile',
      enableRowGroup: false,
      hide: true,
      type: [ColumnType.NumberText],
    },
    {
      field: 'CardReaderSerial',
      headerName: 'Vehicle.CardReaderSerial',
      type: [ColumnType.NumberText],
      hide: true,
    },
    {
      field: 'LastGPSLat',
      headerName: 'Vehicle.LastGPSLat',
      enableRowGroup: false,
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'LastGPSLon',
      headerName: 'Vehicle.LastGPSLon',
      enableRowGroup: false,
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'LastCourse',
      headerName: 'Vehicle.LastCourse',
      enableRowGroup: false,
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'MileageCurrentYear',
      headerName: 'Vehicle.MileageCurrentYear',
      type: [ColumnType.Km],
      hide: true,
    },
    {
      field: 'MileagePreviousYear',
      headerName: 'Vehicle.MileagePreviousYear',
      type: [ColumnType.Km],
      cellStyle: { 'text-align': 'right' },
      hide: true,
      valueFormatter: formatterParams => {
        if (formatterParams.value) {
          const distance = +formatterParams.value;
          return Math.round(distance).toLocaleString('no') + ' km';
        } else {
          return '';
        }
      },
    },
    {
      field: 'ShowRecord',
      headerName: 'Vehicle.ShowRecord',
      type: [ColumnType.Checkbox],
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value ? 'General.True' : 'General.False';
          return this.translate.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
      hide: true,
      suppressColumnsToolPanel: true,
    },
    {
      cellStyle: ({ value }) => {
        if (typeof value !== 'number') {
          return undefined;
        } else if (value < 20) {
          return {
            background: 'var(--dv-bg-surface-danger-active)',
            justifyContent: 'flex-end',
            display: 'flex',
          };
        } else if (value < 50) {
          return {
            background: 'var(--dv-bg-surface-warning-active)',
            justifyContent: 'flex-end',
            display: 'flex',
          };
        }
        return {
          justifyContent: 'flex-end',
          display: 'flex',
        } as CellStyle;
      },
      headerName: 'VehicleServiceGrid.ServiceEUInspectionNextDays',
      field: 'ServiceEUInspectionNextDays',
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'ServiceEUInspectionLastDate',
      headerName: 'Vehicle.ServiceEUInspectionLastDate',
      type: [ColumnType.DateVT],
      hide: true,
    },
    {
      field: 'RegistrationStatus',
      type: ColumnType.Text,
      hide: true,
      headerName: 'Vehicle.RegistrationStatus',
    },
    {
      field: 'FuelTypeDescription',
      headerName: 'FuelTypeID.Description',
      type: [ColumnType.Text],
      cellStyle: { 'text-align': 'left' },
      hide: true,
    },
    {
      field: 'CO2Emission',
      headerName: 'Vehicle.Co2Emission',
      hide: true,
      type: [ColumnType.Number],
    },
    {
      field: 'FuelConsumption',
      headerName: 'Vehicle.FuelConsumptionForElectric',
      type: [ColumnType.NumberDecimal],
      valueGetter: params =>
        isElectricFuelType(params.data?.FuelTypeID)
          ? params.data?.FuelConsumption
          : undefined,
      hide: true,
    },
    { field: 'FuelTypeID', type: ColumnType.Invisible },
    {
      field: 'EmissionClassDescription',
      headerName: 'Vehicle.EmissionClassID.Description',
      type: [ColumnType.Text],
      cellStyle: { 'text-align': 'left' },
      hide: true,
    },
    {
      field: 'ExpenseLocationUseDiscount',
      headerName: 'Vehicle.ExpenseLocationUseDiscount',
      type: [ColumnType.Checkbox],
      hide: true,
    },
    {
      field: 'ServiceComment',
      headerName: 'Vehicle.ServiceComment',
      type: [ColumnType.Text],
      hide: true,
    },
    {
      field: 'ServiceLastDate',
      headerName: 'Vehicle.ServiceLastDate',
      type: [ColumnType.DateVT],
      hide: true,
    },
    {
      field: 'ServiceInMileage',
      headerName: 'VehicleServiceGrid.NextServiceMileage',
      type: [ColumnType.Km],
      hide: true,
      valueFormatter: formatterParams => {
        if (formatterParams.value) {
          const distance = +formatterParams.value;
          return Math.round(distance).toLocaleString('no') + ' km';
        } else {
          return '';
        }
      },
      cellStyle: ({ value }) => {
        if (value && value < 1000 && value >= 100) {
          return {
            'text-align': 'right',
            background: 'var(--dv-bg-surface-warning-active)',
            justifyContent: 'flex-end',
            display: 'flex',
          };
        } else if (value !== null && value < 100) {
          return {
            'text-align': 'right',
            background: 'var(--dv-bg-surface-danger-active)',
            justifyContent: 'flex-end',
            display: 'flex',
          };
        }
        return { 'text-align': 'right', background: '' } as CellStyle;
      },
    },
    {
      field: 'ServiceInDays',
      headerName: 'Vehicle.NextServiceDays',
      type: [ColumnType.Number],
      hide: true,
      cellStyle: ({ value }) => {
        if (value && value < 31 && value >= 1) {
          return {
            'text-align': 'right',
            background: 'var(--dv-bg-surface-warning-active)',
            justifyContent: 'flex-end',
            display: 'flex',
          };
        } else if (value !== null && value < 1) {
          return {
            'text-align': 'right',
            background: 'var(--dv-bg-surface-danger-active)',
            justifyContent: 'flex-end',
            display: 'flex',
          };
        }
        return {
          'text-align': 'right',
          background: '',
          display: 'flex',
          justifyContent: 'flex-end',
        } as CellStyle;
      },
    },
    {
      field: 'NextServiceDate',
      headerName: 'Vehicle.NextServiceDate',
      type: [ColumnType.Date],
      hide: true,
      cellStyle: ({ value }) => {
        if (!value) return undefined;

        return {
          'background-color': NextServiceUtils.getNextServiceDateBg(value),
          'text-align': 'right',
        };
      },
    },
    {
      field: 'ServiceIntervalDays',
      headerName: 'Vehicle.ServiceIntervalDays',
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'AllowShowInMap',
      headerName: 'Vehicle.AllowShowInMap',
      type: [ColumnType.Checkbox],
      hide: true,
    },
    {
      field: 'CustomTripSetup',
      headerName: 'Vehicle.CustomTripSetup',
      type: [ColumnType.Checkbox],
      hide: true,
    },
    {
      field: 'TagDescription',
      headerName: 'Vehicle.TagDescription',
      type: [ColumnType.Text],
      hide: true,
    },
    {
      field: 'FinancingType',
      headerName: 'Vehicle.FinancingType',
      type: [ColumnType.Text],
      hide: true,
    },
    {
      field: 'FinancingRemainingMonths',
      headerName: 'Vehicle.FinancingRemainingMonths',
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'FinancingRemainingKm',
      headerName: 'Vehicle.FinancingRemainingKm',
      type: [ColumnType.Km],
      hide: true,
    },
    {
      field: 'FinancingIdealKmDeviation',
      headerName: 'Vehicle.FinancingIdealKmDeviation',
      type: [ColumnType.Number],
      hide: true,
    },
    {
      field: 'FinancingRemainingKmMonth',
      headerName: 'Vehicle.FinancingRemainingKmMonth',
      type: [ColumnType.Km],
      hide: true,
    },
    {
      field: 'FinancingPeriodEnd',
      headerName: 'Vehicle.FinancingPeriodEnd',
      type: [ColumnType.DateVT],
      hide: true,
    },
    {
      field: 'IsRouteNetworkMaintainer',
      headerName: 'Vehicle.IsRouteNetworkMaintainer',
      type: [ColumnType.Checkbox],
      filterParams: {
        values: [true, false],
        valueFormatter: (data: { value: boolean }) => {
          const key = data.value ? 'General.True' : 'General.False';
          return this.translate.instant(key);
        },
      },
      filter: 'agSetColumnFilter',
      hide: true,
      suppressColumnsToolPanel: !this.metaDataService.hasSkiDomain(),
    },
    { field: 'RouteNetworkMaintainerTypeID', type: ColumnType.Invisible },
    { field: 'UserStatusID', type: ColumnType.Invisible },
    { field: 'DepartmentDeleted', type: ColumnType.Invisible },
  ];
}
